import Dexie, { type Table } from 'dexie';

/**
 * Dexie DB - IndexedDB wrapper para offline-first
 * AIDEV-NOTE: Todas as tabelas são sincronizadas via Op-Log
 */

// ============================================================
// Tipos das entidades
// ============================================================

export interface Cliente {
  codigo: number;
  razaoSocial: string;
  nomeFantasia?: string;
  cnpjCpf: string;
  endereco: string;
  numero: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  telefone?: string;
  celular?: string;
  email?: string;
  latitude?: number;
  longitude?: number;
  updated_at: number;
  // AIDEV-NOTE: Campos adicionados na v2 para suportar filtro por vendedor e controle de crédito
  vendedor_id?: number;
  situacao?: 'A' | 'I' | 'B'; // Ativo, Inativo, Bloqueado
  limite_credito?: number;
}

export interface Produto {
  codigo: number;
  referencia: string;
  descricao: string;
  unidade: string;
  precoVenda?: number;
  estoqueAtual?: number;
  status: 'A' | 'I';
  updated_at: number;
}

export interface Pedido {
  id: string;
  numero?: number;
  cliente_id: number;
  vendedor_id: number;
  status: 'rascunho' | 'finalizado' | 'sincronizando' | 'sincronizado' | 'erro_sync';
  itens: PedidoItem[];
  valor_total: number;
  desconto_total?: number;
  observacoes?: string;
  created_at: number;
  updated_at: number;
  synced_at?: number;
  // AIDEV-NOTE: Campos adicionados na v2 para vínculo com tabela de preço e rastreio ERP
  tabela_preco_id?: number;
  serie?: string;
  numero_pedido_erp?: number;
}

export interface PedidoItem {
  produto_id: number;
  descricao: string;
  quantidade: number;
  preco_unitario: number;
  desconto?: number;
}

// AIDEV-NOTE: Parcelas do pedido — espelho local de pedidos_parcelas (Supabase/Firebird)
export interface PedidoParcela {
  id: string;
  pedido_id: string;
  numero_parcela: number;
  valor: number;
  vencimento: number; // timestamp
  forma_pagamento?: string;
}

export interface Visita {
  id: string;
  cliente_id: number;
  vendedor_id: number;
  tipo: 'checkin' | 'checkout';
  latitude: number;
  longitude: number;
  observacoes?: string;
  created_at: number;
  // AIDEV-NOTE: Campos adicionados na v2 para controle de duração e estado da visita
  duracao_minutos?: number;
  status?: 'planejada' | 'realizada' | 'cancelada';
  pedido_id?: string;
  // AIDEV-NOTE: Campo adicionado na v3 para suportar fotos de visita (URLs ou blob refs)
  fotos?: string[];
}

export interface Lead {
  id: string;
  cliente_id?: number;
  nome: string;
  email?: string;
  telefone?: string;
  status: 'novo' | 'qualificado' | 'proposta' | 'ganho' | 'perdido';
  valor?: number;
  probabilidade?: number;
  observacoes?: string;
  created_at: number;
  updated_at: number;
  // AIDEV-NOTE: Campos adicionados na v2 para pipeline CRM e próxima atividade
  vendedor_id?: number;
  pipeline_etapa?: string;
  proxima_atividade_em?: number; // timestamp
}

// AIDEV-NOTE: Tabelas de preço sincronizadas do Firebird — necessárias para pedidos offline
export interface TabelaPreco {
  codigo: number;
  descricao: string;
  fator_desconto?: number;
  ativo: boolean;
  updated_at: number;
}

// AIDEV-NOTE: Atividades do CRM (ligações, e-mails, tarefas) vinculadas a leads ou clientes
export interface Atividade {
  id: string;
  lead_id?: string;
  cliente_id?: number;
  tipo: 'ligacao' | 'email' | 'tarefa' | 'reuniao' | 'visita';
  descricao: string;
  status: 'pendente' | 'concluida' | 'cancelada';
  data_prevista?: number; // timestamp
  data_realizada?: number; // timestamp
  vendedor_id: number;
  created_at: number;
}

// AIDEV-NOTE: Cache local do vendedor para uso offline (dados do JWT + sync_vendedores)
export interface VendedorCache {
  codigo: number;
  nome: string;
  email?: string;
  perfil: 'admin' | 'gerente' | 'vendedor' | 'representante';
  tabela_preco_padrao?: number;
  updated_at: number;
}

// AIDEV-NOTE: Condições de pagamento sincronizadas do Firebird
export interface CondicaoPagto {
  codigo: number;
  descricao: string;
  qtde_parcelas: number;
  acrescimo?: number;
  desconto?: number;
  updated_at: number;
}

export interface OpLog {
  id: string;
  operation_type: string;
  status: 'pending' | 'syncing' | 'synced' | 'failed';
  entity_type: string;
  entity_id: string | number;
  encrypted_payload: {
    iv: string;
    data: string;
    tag: string;
  };
  retry_count: number;
  last_error?: string;
  created_at: number;
  // AIDEV-NOTE: ja era gravado em runtime por markAsSyncing, mas faltava no tipo. Campo nao
  // indexado (nao exige nova versao do schema Dexie). recuperarOpsTravadas usa como "ultima
  // atividade" para identificar ops orfas presas em 'syncing'.
  updated_at?: number;
  synced_at?: number;
  priority: number;
}

export interface SyncConfig {
  last_sync_at: number;
  is_online: boolean;
  grace_period_expires_at?: number;
}

// ============================================================
// Schema do Dexie
// ============================================================

/**
 * AIDEV-NOTE: Chaves primárias e índices definidos aqui.
 * version(1) é preservada para migração segura de dados existentes.
 * version(2) adiciona novas tabelas e índices aprimorados.
 */
class AzoupDB extends Dexie {
  clientes!: Table<Cliente, number>;
  produtos!: Table<Produto, number>;
  pedidos!: Table<Pedido, string>;
  pedidos_parcelas!: Table<PedidoParcela, string>;
  visitas!: Table<Visita, string>;
  leads!: Table<Lead, string>;
  atividades!: Table<Atividade, string>;
  tabelas_preco!: Table<TabelaPreco, number>;
  vendedor_cache!: Table<VendedorCache, number>;
  condicoes_pagto!: Table<CondicaoPagto, number>;
  op_log!: Table<OpLog, string>;
  sync_config!: Table<SyncConfig, string>;

  constructor() {
    super('AzoupDB');

    // AIDEV-NOTE: version(1) preservada — nunca remover para manter migração segura
    this.version(1).stores({
      clientes: 'codigo, razaoSocial, cidade, uf',
      produtos: 'codigo, referencia, descricao, status',
      pedidos: 'id, cliente_id, vendedor_id, status, created_at',
      visitas: 'id, cliente_id, vendedor_id, created_at',
      leads: 'id, cliente_id, status, created_at',
      op_log: 'id, operation_type, status, entity_type, created_at, priority',
      sync_config: 'key'
    });

    /**
     * AIDEV-NOTE: version(2) — Refinamento do schema para offline completo.
     *
     * Mudanças em tabelas existentes:
     *   - clientes: +cnpjCpf, +updated_at (busca por CPF/CNPJ e delta sync)
     *   - pedidos: +synced_at, +updated_at, +tabela_preco_id (rastreio sync e filtro por tabela)
     *   - leads: +updated_at, +vendedor_id (delta sync e filtro por vendedor)
     *   - op_log: +[status+priority] (índice composto para drain otimizado)
     *
     * Novas tabelas:
     *   - pedidos_parcelas: parcelamento dos pedidos (espelho CARTEIRA_PARCELA_PEDIDO)
     *   - tabelas_preco: preços do Firebird para uso offline
     *   - atividades: CRM — ligações, e-mails, tarefas
     *   - vendedor_cache: dados do vendedor logado para uso offline
     *   - condicoes_pagto: condições de pagamento do Firebird
     */
    this.version(2)
      .stores({
        // Tabelas existentes com índices aprimorados
        clientes: 'codigo, razaoSocial, cidade, uf, cnpjCpf, updated_at',
        produtos: 'codigo, referencia, descricao, status',
        pedidos:
          'id, cliente_id, vendedor_id, status, created_at, synced_at, updated_at, tabela_preco_id',
        visitas: 'id, cliente_id, vendedor_id, created_at',
        leads: 'id, cliente_id, status, created_at, updated_at, vendedor_id',
        // AIDEV-NOTE: Índice composto [status+priority] permite query eficiente no drain do op-log
        op_log: 'id, operation_type, status, entity_type, created_at, priority, [status+priority]',
        sync_config: 'key',

        // Novas tabelas
        pedidos_parcelas: 'id, pedido_id, numero_parcela',
        tabelas_preco: 'codigo, descricao, ativo',
        atividades: 'id, lead_id, cliente_id, tipo, status, vendedor_id, data_prevista, created_at',
        vendedor_cache: 'codigo, email',
        condicoes_pagto: 'codigo, descricao'
      })
      .upgrade((_tx) => {
        // AIDEV-NOTE: Upgrade seguro — apenas reindexação dos registros existentes.
        // Dexie reindexará automaticamente as tabelas existentes com os novos índices.
        // Não há transformação de dados necessária pois os novos campos são opcionais.
        console.log('[AzoupDB] Migrating from v1 to v2 — reindexing existing tables');
      });

    /**
     * AIDEV-NOTE: version(3) — Adiciona campo fotos na tabela visitas.
     *
     * Mudancas:
     *   - visitas: campo `fotos` (string[]) para armazenar URLs/blob refs de fotos capturadas
     *   - Nao altera indices — fotos nao sao indexadas (array de URLs)
     */
    this.version(3)
      .stores({
        // Mantém todos os stores anteriores — Dexie requer redeclaração completa
        clientes: 'codigo, razaoSocial, cidade, uf, cnpjCpf, updated_at',
        produtos: 'codigo, referencia, descricao, status',
        pedidos:
          'id, cliente_id, vendedor_id, status, created_at, synced_at, updated_at, tabela_preco_id',
        visitas: 'id, cliente_id, vendedor_id, created_at',
        leads: 'id, cliente_id, status, created_at, updated_at, vendedor_id',
        op_log: 'id, operation_type, status, entity_type, created_at, priority, [status+priority]',
        sync_config: 'key',
        pedidos_parcelas: 'id, pedido_id, numero_parcela',
        tabelas_preco: 'codigo, descricao, ativo',
        atividades: 'id, lead_id, cliente_id, tipo, status, vendedor_id, data_prevista, created_at',
        vendedor_cache: 'codigo, email',
        condicoes_pagto: 'codigo, descricao'
      })
      .upgrade((_tx) => {
        // AIDEV-NOTE: Upgrade seguro — campo fotos é opcional, sem transformação necessária
        console.log('[AzoupDB] Migrating from v2 to v3 — adding fotos field to visitas');
      });
  }
}

export const db = new AzoupDB();

// AIDEV-NOTE: Export helper para limpar DB (dev)
export async function clearDb(): Promise<void> {
  await db.delete();
  await db.open();
  console.log('Database cleared');
}
